import { useRef, useState, useEffect, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { SelectedElement, deriveLabel } from '../../hooks/useTicketLogger';

interface Props {
  onSelect: (el: SelectedElement, screenshot: string) => void;
  onCancel: () => void;
}

interface HighlightBox {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function SelectionOverlay({ onSelect, onCancel }: Props) {
  const [highlight, setHighlight] = useState<HighlightBox | null>(null);
  const hoveredRef = useRef<Element | null>(null);

  // Set crosshair cursor on body; overlay is pointer-events:none so elementFromPoint
  // sees real page elements rather than the overlay div itself.
  useEffect(() => {
    const prev = document.body.style.cursor;
    document.body.style.cursor = 'crosshair';
    return () => { document.body.style.cursor = prev; };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    const el = document.elementFromPoint(e.clientX, e.clientY);
    if (!el || el.closest('[data-ticket-logger-overlay]')) return;
    hoveredRef.current = el;
    const rect = el.getBoundingClientRect();
    setHighlight({ top: rect.top + window.scrollY, left: rect.left + window.scrollX, width: rect.width, height: rect.height });
  }, []);

  const handleClick = useCallback(async (e: MouseEvent) => {
    if ((e.target as Element | null)?.closest?.('[data-ticket-logger-overlay]')) return;
    e.preventDefault();
    e.stopPropagation();
    const el = hoveredRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const label = deriveLabel(el);

    let screenshot = '';
    try {
      const canvas = await html2canvas(el as HTMLElement, {
        useCORS: true,
        allowTaint: false,
        logging: false,
        scale: Math.min(window.devicePixelRatio, 2),
      });
      screenshot = canvas.toDataURL('image/png').replace('data:image/png;base64,', '');
    } catch {
      // Screenshot failed — proceed without it
    }

    onSelect({ label, rect, element: el }, screenshot);
  }, [onSelect]);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('click', handleClick, true);
    };
  }, [handleMouseMove, handleClick]);

  return (
    <>
      {/* pointer-events:none so elementFromPoint sees real page elements */}
      <div
        data-ticket-logger-overlay
        style={{ position: 'fixed', inset: 0, zIndex: 9990, pointerEvents: 'none' }}
      />

      {/* Highlight box */}
      {highlight && (
        <div
          data-ticket-logger-overlay
          style={{
            position: 'absolute',
            top: highlight.top,
            left: highlight.left,
            width: highlight.width,
            height: highlight.height,
            outline: '2px solid #00c7c4',
            backgroundColor: 'rgba(0, 199, 196, 0.08)',
            pointerEvents: 'none',
            zIndex: 9991,
            borderRadius: 4,
            boxSizing: 'border-box',
          }}
        />
      )}

      {/* Badge */}
      <div
        data-ticket-logger-overlay
        style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9995,
          background: '#052831',
          color: '#fff',
          padding: '8px 16px',
          borderRadius: 999,
          fontSize: 13,
          fontWeight: 600,
          letterSpacing: '0.01em',
          pointerEvents: 'none',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
          whiteSpace: 'nowrap',
        }}
      >
        Click to select element · Esc to cancel
      </div>
    </>
  );
}
